import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Loader2,
  LogOut,
  TrendingUp,
  FileCheck,
  Wallet,
  AlertCircle,
  Clock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { useSEO } from "@/lib/useSEO";

const TOKEN_KEY = "acte_admin_token";

const statusColor = {
  paid: "bg-emerald-100 text-emerald-700",
  pending: "bg-amber-100 text-amber-700",
  failed: "bg-red-100 text-red-700",
  refunded: "bg-slate-200 text-slate-700",
  completed: "bg-emerald-100 text-emerald-700",
  processing: "bg-blue-100 text-blue-700",
};

function Login({ onLogin }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.post("/admin/login", { username, password });
      localStorage.setItem(TOKEN_KEY, r.data.token);
      onLogin(r.data.token);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Autentificare eșuată.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-slate-50 dark:bg-slate-950">
      <Card className="w-full max-w-sm p-8 rounded-2xl">
        <h1 className="font-heading text-2xl font-extrabold text-slate-900 dark:text-white mb-1">
          Admin ActeSimplu
        </h1>
        <p className="text-sm text-slate-500 mb-6">Autentificare administrator</p>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="username">Utilizator</Label>
            <Input
              id="username"
              data-testid="admin-username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="password">Parolă</Label>
            <Input
              id="password"
              data-testid="admin-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
            />
          </div>
          <Button
            data-testid="admin-login-btn"
            type="submit"
            disabled={loading}
            className="w-full rounded-full bg-blue-600 hover:bg-blue-700 h-11 font-semibold"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Autentificare"}
          </Button>
        </form>
      </Card>
    </div>
  );
}

const KPI = ({ icon: Icon, label, value, tint }) => (
  <Card className="p-5 rounded-2xl">
    <div className="flex items-center gap-3">
      <span className={`inline-flex items-center justify-center w-10 h-10 rounded-xl ${tint}`}>
        <Icon className="w-5 h-5" />
      </span>
      <div>
        <p className="text-xs text-slate-500">{label}</p>
        <p className="font-heading text-xl font-extrabold text-slate-900 dark:text-white">{value}</p>
      </div>
    </div>
  </Card>
);

function Dashboard({ token, onLogout }) {
  const [data, setData] = useState(null);
  const [orders, setOrders] = useState([]);
  const [docs, setDocs] = useState([]);
  const [editDoc, setEditDoc] = useState(null);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = async () => {
    try {
      const [d, o, dc] = await Promise.all([
        api.get("/admin/dashboard", auth),
        api.get("/admin/orders", auth),
        api.get("/admin/documents", auth),
      ]);
      setData(d.data);
      setOrders(o.data);
      setDocs(dc.data);
    } catch (e) {
      if (e?.response?.status === 401) onLogout();
    }
  };

  useEffect(() => {
    load();
  }, []);

  const toggleActive = async (slug, active) => {
    try {
      await api.patch(`/admin/documents/${slug}`, { active }, auth);
      setDocs((ds) => ds.map((d) => (d.slug === slug ? { ...d, active } : d)));
      toast.success(active ? "Document activat" : "Document dezactivat");
    } catch (e) {
      toast.error("Eroare la actualizare.");
    }
  };

  const saveDoc = async () => {
    try {
      await api.patch(
        `/admin/documents/${editDoc.slug}`,
        {
          description: editDoc.description,
          long_description: editDoc.long_description,
          ai_instruction: editDoc.ai_instruction,
        },
        auth
      );
      setDocs((ds) => ds.map((d) => (d.slug === editDoc.slug ? editDoc : d)));
      setEditDoc(null);
      toast.success("Document actualizat");
    } catch (e) {
      toast.error("Eroare la salvare.");
    }
  };

  if (!data) {
    return (
      <div className="flex items-center justify-center py-40">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto py-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-2xl font-extrabold text-slate-900 dark:text-white">
            Panou administrare
          </h1>
          <p className="text-sm text-slate-500">ActeSimplu</p>
        </div>
        <Button
          data-testid="admin-logout-btn"
          variant="outline"
          onClick={onLogout}
          className="rounded-full border-slate-300"
        >
          <LogOut className="w-4 h-4 mr-2" /> Ieșire
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        <KPI icon={FileCheck} label="Comenzi totale" value={data.total_orders} tint="bg-blue-100 text-blue-600" />
        <KPI icon={Wallet} label="Comenzi plătite" value={data.paid_orders} tint="bg-emerald-100 text-emerald-600" />
        <KPI icon={TrendingUp} label="Venit total" value={`${data.revenue_ron} lei`} tint="bg-indigo-100 text-indigo-600" />
        <KPI icon={FileCheck} label="Documente generate" value={data.documents_generated} tint="bg-sky-100 text-sky-600" />
        <KPI icon={AlertCircle} label="Erori" value={data.failed} tint="bg-red-100 text-red-600" />
        <KPI icon={Clock} label="Ultimele 24h" value={data.last_24h} tint="bg-amber-100 text-amber-600" />
      </div>

      <Card className="p-5 rounded-2xl mb-8">
        <h3 className="font-heading font-semibold text-slate-900 dark:text-white mb-4">Funnel & conversie</h3>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 text-center">
          {[
            { l: "Vizite", v: data.funnel.page_view },
            { l: "Formulare începute", v: data.funnel.form_started },
            { l: "Checkout-uri", v: data.funnel.checkout_started },
            { l: "Plăți reușite", v: data.funnel.payment_success },
            { l: "Conversie", v: `${data.conversion_rate}%` },
          ].map((x) => (
            <div key={x.l} className="p-3 rounded-xl bg-slate-50 dark:bg-slate-900">
              <p className="font-heading text-xl font-extrabold text-slate-900 dark:text-white">{x.v}</p>
              <p className="text-xs text-slate-500">{x.l}</p>
            </div>
          ))}
        </div>
      </Card>

      <Tabs defaultValue="orders">
        <TabsList className="mb-4">
          <TabsTrigger value="orders" data-testid="tab-orders">Comenzi</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">Documente</TabsTrigger>
        </TabsList>

        <TabsContent value="orders">
          <Card className="rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ID</TableHead>
                    <TableHead>Data</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Tip document</TableHead>
                    <TableHead>Sumă</TableHead>
                    <TableHead>Plată</TableHead>
                    <TableHead>Generare</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {orders.map((o) => (
                    <TableRow key={o.id} data-testid={`order-row-${o.id}`}>
                      <TableCell className="font-mono text-xs">{o.id.slice(0, 8)}</TableCell>
                      <TableCell className="text-xs whitespace-nowrap">
                        {new Date(o.created_at).toLocaleString("ro-RO")}
                      </TableCell>
                      <TableCell className="text-xs">{o.email}</TableCell>
                      <TableCell className="text-xs">{o.document_type}</TableCell>
                      <TableCell className="text-xs">{(o.amount / 100).toFixed(0)} lei</TableCell>
                      <TableCell>
                        <Badge className={`${statusColor[o.payment_status] || ""} rounded-full`}>
                          {o.payment_status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={`${statusColor[o.generation_status] || ""} rounded-full`}>
                          {o.generation_status}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                  {orders.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center text-slate-400 py-8">
                        Nicio comandă încă.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="documents">
          <div className="grid gap-3">
            {docs.map((d) => (
              <Card key={d.slug} className="p-4 rounded-2xl flex items-center justify-between gap-4" data-testid={`admin-doc-${d.slug}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-slate-900 dark:text-white truncate">{d.title}</p>
                    <Badge variant="outline" className="text-xs">{d.category}</Badge>
                  </div>
                  <p className="text-xs text-slate-500 truncate">{d.description}</p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-500">{d.active ? "Activ" : "Inactiv"}</span>
                    <Switch
                      data-testid={`toggle-${d.slug}`}
                      checked={d.active}
                      onCheckedChange={(c) => toggleActive(d.slug, c)}
                    />
                  </div>
                  <Button
                    data-testid={`edit-doc-${d.slug}`}
                    size="sm"
                    variant="outline"
                    onClick={() => setEditDoc(d)}
                    className="rounded-full"
                  >
                    Editează
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={!!editDoc} onOpenChange={(o) => !o && setEditDoc(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Editează: {editDoc?.title}</DialogTitle>
          </DialogHeader>
          {editDoc && (
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label>Descriere scurtă</Label>
                <Textarea
                  data-testid="edit-description"
                  rows={2}
                  value={editDoc.description || ""}
                  onChange={(e) => setEditDoc({ ...editDoc, description: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Descriere lungă</Label>
                <Textarea
                  rows={3}
                  value={editDoc.long_description || ""}
                  onChange={(e) => setEditDoc({ ...editDoc, long_description: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Instrucțiune AI (prompt)</Label>
                <Textarea
                  data-testid="edit-prompt"
                  rows={4}
                  value={editDoc.ai_instruction || ""}
                  onChange={(e) => setEditDoc({ ...editDoc, ai_instruction: e.target.value })}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDoc(null)} className="rounded-full">
              Anulează
            </Button>
            <Button data-testid="save-doc-btn" onClick={saveDoc} className="rounded-full bg-blue-600 hover:bg-blue-700">
              Salvează
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Admin() {
  useSEO("Admin | ActeSimplu");
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };
  if (!token) return <Login onLogin={setToken} />;
  return <Dashboard token={token} onLogout={logout} />;
}
